/**
 * Request Tracker
 *
 * Correlates async WebSocket request/response pairs by ID.
 * Handles timeouts and cleanup.
 *
 * Ported from: request_tracker.rs (82 lines)
 */

import { TransportError } from './types';

/**
 * Response callback type
 */
type ResponseCallback = (result: any | Error) => void;

/**
 * Request Tracker
 *
 * Manages pending WebSocket requests, correlating responses by ID.
 * Each request gets a unique ID and a callback that fires when response arrives.
 */
export class RequestTracker {
  private idCounter = 1;
  private pendingRequests = new Map<number, ResponseCallback>();

  /**
   * Generate next unique request ID
   *
   * Rust equivalent: id_counter.fetch_add(1, Ordering::SeqCst)
   */
  nextId(): number {
    return this.idCounter++;
  }

  /**
   * Track a request and return promise that resolves when response arrives
   *
   * @param id - Request ID
   * @param timeoutMs - Timeout in milliseconds (default 30s)
   * @returns Promise that resolves with response data or rejects on timeout/error
   *
   * Rust equivalent:
   * ```rust
   * pub async fn track_request(&self, id: u64) -> Result<Value, ClientError> {
   *   let (tx, rx) = oneshot_channel();
   *   self.pending_requests.insert(id, tx);
   *   match timeout(Duration::from_secs(30), rx).await {
   *     Ok(Ok(result)) => result,
   *     Ok(Err(_)) => Err(ClientError::Network("Request cancelled".to_string())),
   *     Err(_) => Err(ClientError::Network("Request timed out".to_string()))
   *   }
   * }
   * ```
   */
  trackRequest(id: number, timeoutMs: number = 30000): Promise<any> {
    return new Promise((resolve, reject) => {
      // Setup timeout
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new TransportError(`Request ${id} timed out after ${timeoutMs}ms`, 'TIMEOUT'));
      }, timeoutMs);

      // Store callback
      this.pendingRequests.set(id, (result) => {
        clearTimeout(timeoutHandle);

        if (result instanceof Error) {
          reject(result);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Complete a request with response data
   *
   * @param id - Request ID
   * @param result - Response data or Error
   *
   * Rust equivalent:
   * ```rust
   * pub fn complete_request(&self, id: u64, result: Result<Value, ClientError>) {
   *   if let Some((_, callback)) = self.pending_requests.remove(&id) {
   *     let _ = callback.send(result);
   *   }
   * }
   * ```
   */
  completeRequest(id: number, result: any): void {
    const callback = this.pendingRequests.get(id);
    if (callback) {
      this.pendingRequests.delete(id);
      callback(result);
    } else {
      console.warn(`No pending request found for ID ${id}`);
    }
  }

  /**
   * Fail a request with error
   *
   * @param id - Request ID
   * @param error - Error object or message
   */
  failRequest(id: number, error: Error | string): void {
    const err = typeof error === 'string' ? new TransportError(error) : error;
    this.completeRequest(id, err);
  }

  /**
   * Cancel all pending requests (e.g., on disconnect)
   *
   * Rust equivalent:
   * ```rust
   * pub fn cancel_all_requests(&self) {
   *   let error = Err(ClientError::Network("Connection lost".to_string()));
   *   for entry in self.pending_requests.iter() {
   *     let (id, _) = entry.pair();
   *     if let Some((_, cb)) = self.pending_requests.remove(id) {
   *       let _ = cb.send(error.clone());
   *     }
   *   }
   * }
   * ```
   */
  cancelAll(reason: string = 'Connection lost'): void {
    const error = new TransportError(reason, 'CONNECTION_LOST');

    for (const [, callback] of this.pendingRequests.entries()) {
      callback(error);
    }

    this.pendingRequests.clear();
    console.log(`❌ Cancelled ${this.pendingRequests.size} pending requests: ${reason}`);
  }

  /**
   * Get count of pending requests
   *
   * Useful for debugging/monitoring.
   */
  pendingCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Check if a request is pending
   */
  hasPendingRequest(id: number): boolean {
    return this.pendingRequests.has(id);
  }

  /**
   * Clear specific request without calling callback
   *
   * Use with caution - normally use completeRequest or failRequest.
   */
  clearRequest(id: number): void {
    this.pendingRequests.delete(id);
  }
}
